import { create } from 'zustand';

export interface MagicCodeLoginState {
  // Form state
  email: string;
  code: string;
  currentStep: 'email' | 'code';

  // UI state
  loading: boolean;
  error: string;

  // Email step state
  emailSent: boolean;

  // Code step state
  codeAttempts: number;
  maxCodeAttempts: number;
}

export interface MagicCodeLoginActions {
  // Form actions
  setEmail: (email: string) => void;
  setCode: (code: string) => void;
  setCurrentStep: (step: 'email' | 'code') => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  clearError: () => void;

  // Email step actions
  setEmailSent: (sent: boolean) => void;

  // Code step actions
  incrementCodeAttempts: () => void;
  resetCodeAttempts: () => void;

  // Navigation actions
  goToCodeStep: () => void;
  goToEmailStep: () => void;

  // Reset actions
  reset: () => void;
  resetToEmailStep: () => void;
}

const initialState: MagicCodeLoginState = {
  email: '',
  code: '',
  currentStep: 'email',
  loading: false,
  error: '',
  emailSent: false,
  codeAttempts: 0,
  maxCodeAttempts: 3,
};

export const useMagicCodeLoginStore = create<
  MagicCodeLoginState & MagicCodeLoginActions
>((set, get) => ({
  ...initialState,

  // Form actions
  setEmail: (email: string) => set({ email }),
  setCode: (code: string) => set({ code }),
  setCurrentStep: (step: 'email' | 'code') => set({ currentStep: step }),

  // UI actions
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string) => set({ error }),
  clearError: () => set({ error: '' }),

  // Email step actions
  setEmailSent: (sent: boolean) => set({ emailSent: sent }),

  // Code step actions
  incrementCodeAttempts: () => {
    const { codeAttempts } = get();
    set({ codeAttempts: codeAttempts + 1 });
  },
  resetCodeAttempts: () => set({ codeAttempts: 0 }),

  // Navigation actions
  goToCodeStep: () =>
    set({
      currentStep: 'code',
      error: '',
      code: '',
    }),
  goToEmailStep: () =>
    set({
      currentStep: 'email',
      error: '',
      emailSent: false,
      code: '',
      codeAttempts: 0,
    }),

  // Reset actions
  reset: () => set({ ...initialState }),
  resetToEmailStep: () =>
    set({
      ...initialState,
      email: get().email, // Keep the email if user wants to try again
    }),
}));
